import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { StorageService } from '@morningstar/shared/base/storage-service';
import { SyncfusionChartSettingsHelper, YAxisHelper } from '@morningstar/shared/util';
import { Chart } from '@syncfusion/ej2-angular-charts';
import { CategoryService } from 'apps/wsg/src/app/shared/categories/category.service';
import { BehaviorSubject, combineLatest, merge, Subject } from 'rxjs';
import { delay, filter, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { DashboardBaseComponent } from '../../dashboard-base/dashboard-base.component';
import { RevenueTotalService } from '../revenue-total.service';

@Component({
  selector: 'wsg-earned-revenue-line',
  templateUrl: './earned-revenue-line.component.html',
  styleUrls: ['../../dashboard.scss', './earned-revenue-line.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ RevenueTotalService ]
})
export class EarnedRevenueLineComponent extends DashboardBaseComponent implements AfterViewInit, OnDestroy {

  title: string = 'Earned Revenue';
  chartId = 'earnedRevenueLineChart';
  @ViewChild('chart') chart: Chart;
  options = { billedOrEarned: 'E' };
  initialChartSettings = SyncfusionChartSettingsHelper.getLineChartSettings();

  // == STATE ===========================================================

  chartSettings = new BehaviorSubject<any>(SyncfusionChartSettingsHelper.getLineChartSettings());
  chartSettings$ = this.chartSettings.asObservable();
  categories$ = this.categoryService.get();

  form = this.formBuilder.group({
    location: 'ALL',
    category: 'ALL'
  });

  // == ACTIONS =========================================================

  constructorLoaded = new Subject();
  constructorLoaded$ = this.constructorLoaded.asObservable();

  // == INTERMEDIATE ====================================================

  location$ = this.form.get('location').valueChanges.pipe(
    startWith('ALL'),
  );

  category$ = this.form.get('category').valueChanges.pipe(
    startWith('ALL'),
  );

  revenueTotal$ = combineLatest([this.location$, this.category$]).pipe(
    filter(([location, category]) => !!location && !!category),
    switchMap(([location, category]) => this.revenueTotalService.getCategoryTrendLine(location, category, this.options))
  );

  categoryName$ = combineLatest([this.categories$, this.category$]).pipe(
    map(([categories, category]) => {
      if (category === 'ALL') {
        return 'All Billing Categories';
      } else {
        return categories.find(c => c.id === category).description;
      }
    })
  );

  formattedChartSettings$ = this.revenueTotal$.pipe(
    withLatestFrom(this.chartSettings$),
    map(([revenue, chartSettings]) => {
      if (revenue && revenue.length > 0) {
        const maxValueFromArray = Math.max(...revenue.map(r => r.revenue), 0);
        const minValueFromArray = Math.min(...revenue.map(r => r.revenue));

        let nearestOrderOfMagnitude = YAxisHelper.getNearestOrderOfMagnitdue(maxValueFromArray);

        if (nearestOrderOfMagnitude === 0) {
          nearestOrderOfMagnitude = 1;
        }

        const chartMax = Math.ceil(maxValueFromArray / nearestOrderOfMagnitude) * nearestOrderOfMagnitude;
        const chartMin = Math.floor(minValueFromArray / nearestOrderOfMagnitude) * nearestOrderOfMagnitude;

        chartSettings.primaryYAxis['maximum'] = chartMax;
        chartSettings.primaryYAxis['minimum'] = chartMin;
        chartSettings.primaryYAxis['interval'] = Math.round(Math.ceil((chartMax - chartMin) / 5));
        chartSettings.tooltip = chartSettings.tooltip;
      }

      return chartSettings;
    })
  );

  // == SIDE EFFECTS ====================================================

  onGetRevenueTotal$ = combineLatest([this.revenueTotal$, this.formattedChartSettings$]).pipe(
    filter(_ => !!this.chart),
    tap(([revenue, chartSettings]) => {
      this.chart.primaryYAxis = chartSettings.primaryYAxis;
      this.chart.primaryXAxis = chartSettings.primaryXAxis;
      this.chart.chartArea = chartSettings.chartArea;

      this.chart.dataSource = revenue;
      this.chart.refresh();
    })
  );

  // == SUBSCRIPTION ====================================================

  subscription = merge(
    this.onGetRevenueTotal$,
    this.formattedChartSettings$
  ).subscribe();

  // == LIFECYCLE =======================================================

  constructor(private revenueTotalService: RevenueTotalService,
              private formBuilder: FormBuilder,
              private categoryService: CategoryService,
              protected storageService: StorageService) {
    super(storageService);

    this.constructorLoaded.next();
  }

  ngAfterViewInit() {
    this.subscription.add(this.theme$.pipe(
      delay(50),
      filter(_ => !!this.chart),
      tap(_ => this.chart.refresh())
    ).subscribe())
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
